// OcrFields.jsx
import React, { useContext, useEffect, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { TextField } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import DocumentScannerOutlinedIcon from "@mui/icons-material/DocumentScannerOutlined";
import TagIcon from "@mui/icons-material/Tag";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import { useDrop } from "react-dnd";
import OcrFullDataDialog from "./OcrFullDataDialog";
import getOcrKeys from "../../../api/getOcrKeys";
import { AuthContext } from "../../../context/Auth/AuthContext";
import baseUrl from "../../../config/baseUrl";
import getOcrData from "../../../api/getOcrData";

const EMPTY_VALUES = ["", "null", "undefined", "n/a", "na", "none", "not found"];

const isFound = (value) =>
  value !== null &&
  value !== undefined &&
  !EMPTY_VALUES.includes(String(value).trim().toLowerCase());

const LONG_FIELD_PATTERN = /address|description|remarks|notes|comment/i;

// Long fields (address, etc.) span both columns; everything else is a
// half-width card, matching the reference's 2-column grid.
const isLongField = (fieldName, value) =>
  LONG_FIELD_PATTERN.test(fieldName) || String(value || "").length > 30;

const getFieldIcon = (fieldName) => {
  const name = fieldName.toLowerCase();
  if (/address|state|city|zip|postal/.test(name)) return PlaceOutlinedIcon;
  if (/country|nationality/.test(name)) return PublicOutlinedIcon;
  if (/date|expiry|expiration|dob|birth|issued/.test(name)) return EventOutlinedIcon;
  if (/phone|mobile/.test(name)) return PhoneOutlinedIcon;
  if (/email/.test(name)) return MailOutlineIcon;
  if (/name/.test(name)) return PersonOutlineIcon;
  if (/alien|passport|license|licence|ssn|id/.test(name)) return BadgeOutlinedIcon;
  if (/number|no\b|num/.test(name)) return TagIcon;
  return NotesOutlinedIcon;
};

const OcrFields = ({
  control,
  errors,
  setValue,
  subCategory,
  documentID,
  showFields,
  setShowFields,
  performOcr,
  highlight,
}) => {
  const { token } = useContext(AuthContext);
  const [ocrKeys, setOcrKeys] = useState({});
  // Cards are read-only by default; the pencil on a card switches just that
  // card to an input. Values stay bound to the same `ocr_<field>` form
  // Controllers and are still saved by the viewer's overall Submit button.
  const [editingFields, setEditingFields] = useState({});
  // Read-only "View" dialog for the full-document OCR JSON.
  const [showFullData, setShowFullData] = useState(false);

  // Set up the drop area
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: "DATA",
    drop: (item) => {
      // Set the form field based on the dropped item's fieldName
      setValue(item.fieldName, item.data);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  // Fetch OCR keys from the API
  const fetchOcrKeys = async () => {
    const url = `${baseUrl}/api/get-subtype-ocr-keys?document_sub_category=${subCategory}`;

    try {
      const { data } = await getOcrKeys({ api: url, token });
      if (data && data[0] && data[0].document_identifier) {
        try {
          const parsedOcrKeys = JSON.parse(data[0].document_identifier);
          setOcrKeys(parsedOcrKeys);
        } catch (parseError) {
          console.error("Error parsing document_identifier:", parseError);
        }
      } else {
        console.error("Invalid OCR keys data structure:", data);
      }
    } catch (err) {
      console.error("Error fetching OCR keys:", err);
    }
  };

  const getOcrDetails = async () => {
    const url = `${baseUrl}/api/get-ocr-data?document_id=${documentID}`;
    try {
      const res = await getOcrData({ api: url, token });
      if (res && res.output && res.output.status === "success") {
        setShowFields(true);
      } else {
        setShowFields(false);
      }
    } catch (err) {
      console.error("Error fetching OCR data:", err);
    }
  };

  useEffect(() => {
    fetchOcrKeys();
    // getOcrDetails();
  }, [subCategory]);

  const capitalizeText = (text) => {
    const words = text.split("_");

    const capitalizedText = words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

    return capitalizedText;
  };

  const fieldNames = Object.keys(ocrKeys);
  // Read-only subscription to the current OCR values, only used for the
  // "X of Y found" summary - doesn't change how the fields are bound.
  // Watches the whole form (not a name list): useWatch only computes its
  // value at mount, and fieldNames is still empty then (the OCR keys load
  // asynchronously), so a name-based watch stayed [] -> "0 of N found" after
  // switching tabs / reopening the viewer until some value changed. The whole
  // form values are current at mount and stay updated, and the count is
  // derived from them on every render using the same isFound check that
  // decides which fields show in the found vs. not-found groups.
  const formValues = useWatch({ control });
  const foundCount = fieldNames.filter((fieldName) =>
    isFound(formValues?.[`ocr_${fieldName}`])
  ).length;

  // editingFields[fieldName] holds the group ("found" / "missing") the field
  // was in when editing started, so a field doesn't jump between sections
  // (and lose input focus) while the user is typing in it.
  const toggleEdit = (fieldName, group) =>
    setEditingFields((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName] ? false : group,
    }));

  const hasFields = showFields && fieldNames.length > 0;
  const foundPercent = fieldNames.length
    ? Math.round((foundCount / fieldNames.length) * 100)
    : 0;

  // Same isFound check as the count; a field being edited stays in its group.
  const groupOf = (fieldName) =>
    editingFields[fieldName] ||
    (isFound(formValues?.[`ocr_${fieldName}`]) ? "found" : "missing");
  const foundFields = fieldNames.filter((f) => groupOf(f) === "found");
  const missingFields = fieldNames.filter((f) => groupOf(f) === "missing");

  // Every field keeps its own Controller (same name/default as before), so
  // values, validation, drag-and-drop and Submit behave exactly as before;
  // only the presentation differs between the two groups.
  const renderField = (fieldName, group) => (
    <Controller
      key={fieldName}
      name={`ocr_${fieldName}`} // Prefix OCR fields with "ocr_" to distinguish
      control={control}
      defaultValue="" // Ensure default value is set
      render={({ field: { onChange, onBlur, value, name } }) => {
        const editing = !!editingFields[fieldName];
        const FieldIcon = getFieldIcon(fieldName);
        const label = capitalizeText(fieldName);

        const editor = (
          <TextField
            onChange={onChange}
            onBlur={onBlur}
            // A "Not found" (or other empty marker) value is shown as a blank
            // input instead of looking like real data; the stored value is
            // unchanged until the user types.
            value={isFound(value) ? value : ""}
            placeholder="Enter value"
            name={name}
            variant="outlined"
            size="small"
            fullWidth
            autoFocus
            error={!!errors[fieldName]}
            helperText={errors[fieldName] ? errors[fieldName].message : ""}
            className={canDrop ? "border border-green-500" : ""}
          />
        );
        const doneButton = (
          <button
            type="button"
            onClick={() => toggleEdit(fieldName, group)}
            title="Done"
            aria-label={`Done editing ${label}`}
            className="shrink-0 mt-1 rounded p-0.5 text-nbl-primary hover:bg-nbl-tint"
          >
            <CheckIcon sx={{ fontSize: 15 }} />
          </button>
        );

        if (group === "found") {
          return (
            <div
              className={`ocr-field-card rounded-lg border border-[#b9ecc9] bg-[#ecfaf1] px-2 py-1.5 ${
                editing || isLongField(fieldName, value) ? "col-span-full" : ""
              }`}
            >
              <div className="flex items-center gap-1">
                <span className="flex min-w-0 items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-[#2f8a57]">
                  <FieldIcon sx={{ fontSize: 12 }} />
                  <span className="truncate" title={label}>{label}</span>
                </span>
              </div>
              <div className="mt-0.5 flex items-start justify-between gap-1">
                {editing ? (
                  <>
                    {editor}
                    {doneButton}
                  </>
                ) : (
                  <>
                    <span className="min-w-0 break-words text-[13px] font-bold leading-snug text-[#0f2e22]">
                      {value}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleEdit(fieldName, group)}
                      title="Edit"
                      aria-label={`Edit ${label}`}
                      className="shrink-0 rounded-md bg-white/80 p-0.5 text-gray-500 hover:bg-white hover:text-[#2f8a57]"
                    >
                      <EditOutlinedIcon sx={{ fontSize: 13 }} />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        }

        // Not found: compact neutral row with an "Add" (edit) action.
        return editing ? (
          <div className="ocr-field-card col-span-full rounded-md border border-gray-200 bg-white px-2 py-1.5">
            <span className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
              <FieldIcon sx={{ fontSize: 12 }} />
              {label}
            </span>
            <div className="mt-0.5 flex items-start gap-1">
              {editor}
              {doneButton}
            </div>
          </div>
        ) : (
          <div className="ocr-field-card flex min-w-0 items-center justify-between gap-1 rounded-lg border border-[#e3e7ec] bg-white px-2 py-1">
            <span className="min-w-0">
              <span className="block truncate text-xs text-gray-700" title={label}>
                {label}
              </span>
              <span className="block text-[10px] italic leading-tight text-gray-400">
                Not found
              </span>
            </span>
            <button
              type="button"
              onClick={() => toggleEdit(fieldName, group)}
              title={`Add ${label}`}
              aria-label={`Add ${label}`}
              className="shrink-0 rounded-md bg-[#e4eeff] px-2 py-0.5 text-[11px] font-semibold text-[#1d5fd6] hover:bg-[#d4e4ff]"
            >
              Add
            </button>
          </div>
        );
      }}
    />
  );

  return (
    <div
      ref={drop}
      className={`ocr-fields-panel rounded-lg border ${
        highlight ? "border-nbl-accent ring-2 ring-nbl-accent/40" : "border-[#d7ecef]"
      } ${isOver ? "bg-[#e8f6f7]" : "bg-white"} transition-colors duration-500`}
    >
      {/* Header: title, "X of Y found on this document", percentage */}
      <div className="flex items-center justify-between gap-2 px-2.5 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#e3f5f8] text-[#1f6f7f]">
            <DocumentScannerOutlinedIcon sx={{ fontSize: 17 }} />
          </span>
          <span className="min-w-0">
            <span className="block text-[13px] font-semibold leading-tight text-gray-900">
              OCR Extracted Fields
            </span>
            <span className="block text-[11px] leading-tight text-gray-500">
              {hasFields
                ? `${foundCount} of ${fieldNames.length} found on this document`
                : "Auto-read from the document"}
            </span>
          </span>
        </div>
        {hasFields && (
          <span
            className="shrink-0 text-base font-bold leading-none text-[#1d5fd6]"
            title={`${foundCount} of ${fieldNames.length} fields found`}
          >
            {foundPercent}%
          </span>
        )}
      </div>

      {hasFields ? (
        <div className="flex max-h-[420px] flex-col gap-2 overflow-y-auto px-2 pb-2">
          {foundFields.length > 0 && (
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {foundFields.map((fieldName) => renderField(fieldName, "found"))}
            </div>
          )}

          {missingFields.length > 0 && (
            <div className="rounded-lg border border-dashed border-[#d8dde3] bg-[#f8f9fb] p-1.5">
              <div className="mb-1.5 flex items-center justify-between gap-2 px-0.5">
                <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[#374151]">
                  <span className="flex h-5 w-5 items-center justify-center rounded bg-[#eceff3]">
                    <RadioButtonUncheckedOutlinedIcon sx={{ fontSize: 12 }} className="text-gray-500" />
                  </span>
                  Not found · needs manual entry
                </span>
                <span className="shrink-0 text-[10px] text-gray-400">
                  {missingFields.length} field{missingFields.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {missingFields.map((fieldName) => renderField(fieldName, "missing"))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="px-2.5 pb-3 pt-1 text-xs text-gray-400">
          No OCR data yet. Run "Perform OCR" to extract fields.
        </div>
      )}

      {/* Opens the "Extracted Document Data" panel (full-document OCR data). */}
      {hasFields && (
        <div className="px-2 pb-2">
          <button
            type="button"
            onClick={() => setShowFullData(true)}
            className="flex w-full items-center gap-1.5 rounded-lg border border-[#cdebf2] bg-[#e7f6fa] px-2.5 py-1.5 text-xs font-semibold text-[#0f4c5c]"
          >
            <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
            Extracted Document Insights
            <ExpandMoreIcon sx={{ fontSize: 18 }} className="ml-auto" />
          </button>
        </div>
      )}
      <OcrFullDataDialog
        open={showFullData}
        onClose={() => setShowFullData(false)}
        documentID={documentID}
      />
    </div>
  );
};

export default OcrFields;
